import React, { useEffect, useRef } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import Placeholder from '@tiptap/extension-placeholder';
// TipTap v3 ships TextStyle / Color / FontFamily / FontSize as named exports
// from a single package. Importing them individually (older path) raises a
// "default export not found" build error.
import { TextStyle, Color, FontFamily, FontSize } from '@tiptap/extension-text-style';
import {
  Bold, Italic, Underline as UnderlineIcon, List, ListOrdered, Link as LinkIcon,
  Image as ImageIcon, Eraser, Heading1, Heading2, Heading3, Quote, Undo, Redo,
} from 'lucide-react';

const FONTS = [
  { value: '', label: 'Default' },
  { value: 'Inter, sans-serif', label: 'Sans-serif' },
  { value: 'Georgia, serif', label: 'Serif' },
  { value: 'Menlo, monospace', label: 'Monospace' },
  { value: 'Fredoka, sans-serif', label: 'Fredoka' },
  { value: 'Comic Sans MS, cursive', label: 'Comic Sans' },
];

const SIZES = [
  { value: '',     label: 'Default' },
  { value: '12px', label: 'Small' },
  { value: '14px', label: 'Normal' },
  { value: '18px', label: 'Large' },
  { value: '24px', label: 'Huge' },
];

const COLORS = [
  '#1E1E24', '#FF3B6B', '#FF7A2E', '#FFD166',
  '#06D6A0', '#118AB2', '#9333EA', '#6B6B7A',
];

export default function RichTextEditor({
  value = '',
  onChange,
  placeholder = "Type your message…",
  minHeight = 220,
  testId = 'rte',
  // Optional async (File) => string|{url} uploader. When provided, clicking the
  // Image toolbar button opens a file picker and inserts the uploaded image at
  // the cursor. When omitted, the legacy "paste an Image URL" prompt is used.
  onImageUpload = null,
  imageUploadAccept = 'image/*',
}) {
  const fileInputRef = useRef(null);
  const editor = useEditor({
    extensions: [
      // TipTap v3 StarterKit already bundles Link + Underline; configure
      // StarterKit to drop them so our explicit `Link.configure({ openOnClick:
      // false … })` and Underline imports remain authoritative without
      // triggering the "Duplicate extension names" warning.
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
        link: false,
        underline: false,
      }),
      Underline,
      Link.configure({
        openOnClick: false,
        HTMLAttributes: { rel: 'noopener noreferrer', target: '_blank' },
      }),
      Image.configure({ inline: false }),
      TextStyle,
      Color,
      FontFamily.configure({ types: ['textStyle'] }),
      FontSize,
      Placeholder.configure({ placeholder }),
    ],
    content: value || '',
    onUpdate: ({ editor: ed }) => {
      // TipTap returns "<p></p>" for empty — normalize to '' so the parent's
      // "body required" validation works as expected.
      const html = ed.getHTML();
      onChange?.(html === '<p></p>' ? '' : html);
    },
    editorProps: {
      attributes: {
        class: 'cb-rte-content focus:outline-none',
        'data-testid': `${testId}-content`,
      },
    },
  });

  // Sync external value -> editor (for reply/forward when parent resets the body)
  const lastSetValueRef = useRef(value);
  useEffect(() => {
    if (!editor) return;
    if (value !== lastSetValueRef.current && value !== editor.getHTML()) {
      editor.commands.setContent(value || '', false);
      lastSetValueRef.current = value;
    }
  }, [value, editor]);

  if (!editor) return null;

  const setLink = () => {
    const prev = editor.getAttributes('link').href || '';
    const url = window.prompt('Enter URL (leave blank to remove):', prev);
    if (url === null) return;
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
    } else {
      const safe = /^https?:\/\//i.test(url) ? url : `https://${url}`;
      editor.chain().focus().extendMarkRange('link').setLink({ href: safe }).run();
    }
  };

  const setImage = () => {
    // If an upload handler was provided, prefer the file picker (better UX
    // than asking customers to paste a public URL).
    if (onImageUpload && fileInputRef.current) {
      fileInputRef.current.value = ''; // allow re-selecting the same file
      fileInputRef.current.click();
      return;
    }
    const url = window.prompt('Image URL:');
    if (!url) return;
    editor.chain().focus().setImage({ src: url, alt: 'image' }).run();
  };

  const handleFilePicked = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !onImageUpload) return;
    try {
      const res = await onImageUpload(file);
      const src = typeof res === 'string' ? res : (res?.url || '');
      if (!src) throw new Error('Upload returned no URL');
      editor.chain().focus().setImage({ src, alt: file.name || 'image' }).run();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Image upload failed', err);
      window.alert('Image upload failed — please try a different file.');
    }
  };

  const setFont = (val) => {
    if (val) editor.chain().focus().setFontFamily(val).run();
    else     editor.chain().focus().unsetFontFamily().run();
  };

  const setSize = (val) => {
    if (val) editor.chain().focus().setFontSize(val).run();
    else     editor.chain().focus().unsetFontSize().run();
  };

  const setColor = (color) => {
    editor.chain().focus().setColor(color).run();
  };

  const isActive = (type, attrs) => editor.isActive(type, attrs);

  return (
    <div
      className="border border-[#E8E5DF] rounded-xl overflow-hidden bg-white focus-within:ring-2 focus-within:ring-[#FF3B6B]/40"
      data-testid={`${testId}-wrapper`}
    >
      <div className="flex flex-wrap items-center gap-1 border-b border-[#F0EDE6] bg-[#FFFDF8] px-2 py-1.5">
        <ToolBtn onClick={() => editor.chain().focus().toggleBold().run()}      active={isActive('bold')}      title="Bold (⌘B)"          testId={`${testId}-bold`}>
          <Bold className="w-3.5 h-3.5" />
        </ToolBtn>
        <ToolBtn onClick={() => editor.chain().focus().toggleItalic().run()}    active={isActive('italic')}    title="Italic (⌘I)"        testId={`${testId}-italic`}>
          <Italic className="w-3.5 h-3.5" />
        </ToolBtn>
        <ToolBtn onClick={() => editor.chain().focus().toggleUnderline().run()} active={isActive('underline')} title="Underline (⌘U)"     testId={`${testId}-underline`}>
          <UnderlineIcon className="w-3.5 h-3.5" />
        </ToolBtn>
        <Divider />
        <ToolBtn onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} active={isActive('heading', { level: 1 })} title="Heading 1">
          <Heading1 className="w-3.5 h-3.5" />
        </ToolBtn>
        <ToolBtn onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} active={isActive('heading', { level: 2 })} title="Heading 2">
          <Heading2 className="w-3.5 h-3.5" />
        </ToolBtn>
        <ToolBtn onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} active={isActive('heading', { level: 3 })} title="Heading 3">
          <Heading3 className="w-3.5 h-3.5" />
        </ToolBtn>
        <Divider />
        <ToolBtn onClick={() => editor.chain().focus().toggleBulletList().run()}  active={isActive('bulletList')}  title="Bullet list" testId={`${testId}-bullet`}>
          <List className="w-3.5 h-3.5" />
        </ToolBtn>
        <ToolBtn onClick={() => editor.chain().focus().toggleOrderedList().run()} active={isActive('orderedList')} title="Numbered list">
          <ListOrdered className="w-3.5 h-3.5" />
        </ToolBtn>
        <ToolBtn onClick={() => editor.chain().focus().toggleBlockquote().run()}  active={isActive('blockquote')}  title="Quote">
          <Quote className="w-3.5 h-3.5" />
        </ToolBtn>
        <Divider />
        <ToolBtn onClick={setLink}  active={isActive('link')} title="Link" testId={`${testId}-link`}>
          <LinkIcon className="w-3.5 h-3.5" />
        </ToolBtn>
        <ToolBtn onClick={setImage} title="Image" testId={`${testId}-image`}>
          <ImageIcon className="w-3.5 h-3.5" />
        </ToolBtn>
        <Divider />
        <select
          onChange={(e) => setFont(e.target.value)}
          value={editor.getAttributes('textStyle').fontFamily || ''}
          className="h-7 text-[11px] rounded-md border border-[#E8E5DF] bg-white px-1"
          title="Font family"
          data-testid={`${testId}-font-family`}
        >
          {FONTS.map((f) => <option key={f.label} value={f.value}>{f.label}</option>)}
        </select>
        <select
          onChange={(e) => setSize(e.target.value)}
          value={editor.getAttributes('textStyle').fontSize || ''}
          className="h-7 text-[11px] rounded-md border border-[#E8E5DF] bg-white px-1"
          title="Font size"
          data-testid={`${testId}-font-size`}
        >
          {SIZES.map((s) => <option key={s.label} value={s.value}>{s.label}</option>)}
        </select>
        <ColorPicker
          value={editor.getAttributes('textStyle').color || ''}
          onChange={setColor}
          testId={`${testId}-color`}
        />
        <Divider />
        <ToolBtn onClick={() => editor.chain().focus().unsetAllMarks().clearNodes().run()} title="Clear formatting" testId={`${testId}-clear`}>
          <Eraser className="w-3.5 h-3.5" />
        </ToolBtn>
        <div className="flex-1" />
        <ToolBtn onClick={() => editor.chain().focus().undo().run()} title="Undo (⌘Z)" disabled={!editor.can().undo()}>
          <Undo className="w-3.5 h-3.5" />
        </ToolBtn>
        <ToolBtn onClick={() => editor.chain().focus().redo().run()} title="Redo (⌘⇧Z)" disabled={!editor.can().redo()}>
          <Redo className="w-3.5 h-3.5" />
        </ToolBtn>
      </div>
      <div
        className="px-3 py-2 overflow-y-auto"
        style={{ minHeight }}
        onClick={() => editor.chain().focus().run()}
      >
        <EditorContent editor={editor} />
      </div>
      {/* Hidden file input — used when `onImageUpload` is provided. */}
      {onImageUpload && (
        <input
          ref={fileInputRef}
          type="file"
          accept={imageUploadAccept}
          onChange={handleFilePicked}
          className="hidden"
          data-testid={`${testId}-image-input`}
        />
      )}
      {/* Light styling for the editable surface, scoped via cb-rte-content */}
      <style>{`
        .cb-rte-content { font-size: 14px; line-height: 1.55; color: #1E1E24; }
        .cb-rte-content p { margin: 0 0 0.6em 0; }
        .cb-rte-content h1 { font-size: 22px; font-weight: 700; margin: .4em 0 .35em; }
        .cb-rte-content h2 { font-size: 18px; font-weight: 700; margin: .4em 0 .3em; }
        .cb-rte-content h3 { font-size: 16px; font-weight: 700; margin: .4em 0 .25em; }
        .cb-rte-content ul, .cb-rte-content ol { padding-left: 1.4em; margin: 0 0 .6em 0; }
        .cb-rte-content ul { list-style-type: disc; }
        .cb-rte-content ol { list-style-type: decimal; }
        .cb-rte-content li { margin: .2em 0; }
        .cb-rte-content li > p { margin: 0; }
        .cb-rte-content blockquote { border-left: 3px solid #E8E5DF; padding-left: 12px; color: #6B6B7A; margin: 0 0 .6em 0; }
        .cb-rte-content a { color: #FF3B6B; text-decoration: underline; }
        .cb-rte-content img { max-width: 100%; border-radius: 8px; }
        .cb-rte-content u { text-decoration: underline; }
        .cb-rte-content p.is-editor-empty:first-child::before {
          content: attr(data-placeholder); color: #9a9aa6; pointer-events: none; float: left; height: 0;
        }
      `}</style>
    </div>
  );
}

const ToolBtn = ({ children, onClick, active, disabled, title, testId }) => (
  <button
    type="button"
    onClick={onClick}
    title={title}
    disabled={disabled}
    data-testid={testId}
    className={`h-7 w-7 flex items-center justify-center rounded-md transition disabled:opacity-30 ${
      active
        ? 'bg-[#FF3B6B] text-white'
        : 'text-[#6B6B7A] hover:bg-white hover:text-[#1E1E24]'
    }`}
  >
    {children}
  </button>
);

const Divider = () => <span className="h-5 w-px bg-[#E8E5DF] mx-0.5" aria-hidden />;

const ColorPicker = ({ value, onChange, testId }) => {
  const [open, setOpen] = React.useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);
  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="h-7 px-1.5 rounded-md text-[11px] flex items-center gap-1 text-[#6B6B7A] hover:bg-white hover:text-[#1E1E24]"
        title="Text color"
        data-testid={testId}
      >
        <span
          className="w-4 h-4 rounded-sm border border-[#E8E5DF]"
          style={{ background: value || 'linear-gradient(135deg,#FF3B6B,#FFD166,#06D6A0)' }}
        />
        <span className="uppercase tracking-wider text-[10px]">A</span>
      </button>
      {open && (
        <div className="absolute z-30 mt-1 left-0 bg-white border border-[#E8E5DF] rounded-xl shadow-lg p-2 grid grid-cols-4 gap-1.5">
          {COLORS.map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => { onChange(c); setOpen(false); }}
              className="w-6 h-6 rounded-md border border-[#E8E5DF] hover:scale-110 transition"
              style={{ background: c }}
              title={c}
              aria-label={`Color ${c}`}
              data-testid={`${testId}-${c.replace('#','')}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};
